import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpIcon, ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { footerInfo, studios } from '../data/landing';
import { BrandLogo } from './BrandLogo';
import { FooterNetwork } from './FooterNetwork';
function StudioAddress() {
  return <address data-pixel-avoid className="space-y-6 not-italic md:space-y-8" data-id="element-123" data-mp="3uf0mr7ucs6s8">
      {studios.map(studio => <div key={studio.city} className="text-[15px] leading-[1.4]" data-id="element-124" data-mp="3991alritibr">
          <p className="mb-1 text-[12px] font-medium uppercase tracking-[0.04em] text-white/60" data-id="element-125" data-mp="36d3qyjl4vnu">{studio.city}</p>
          {studio.lines.map(line => <p key={line} data-id="element-126" data-mp="3qcdpgrewxtnn">{line}</p>)}
          <a href={`tel:${studio.phone.replace(/[^+\d]/g, '')}`} className="mt-2 block text-white/50 transition-colors duration-150 hover:text-white" data-id="element-127" data-mp="3qf9s2t1ph6if">
            {studio.phone}
          </a>
        </div>)}
      <a href={`mailto:${footerInfo.email}`} className="-mt-4 block text-[15px] transition-opacity duration-150 hover:opacity-70 md:-mt-6" data-id="element-128" data-mp="3lalgfg6ys7uh">
        {footerInfo.email}
      </a>
    </address>;
}
export function Footer() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();

  // Strong parallax: content rises from far below as the footer scrolls into view.
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end end']
  });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['-45%', '0%']);
  const opacity = useTransform(scrollYProgress, [0, 0.6], reduce ? [1, 1] : [0.2, 1]);
  return <footer ref={ref} id="contact" className="relative overflow-hidden bg-black text-white" data-id="element-129" data-mp="ctgyh86nu0ph">
      {/* Quiet pixel neural network in the empty space; fades around every [data-pixel-avoid] element */}
      <FooterNetwork className="z-0" data-id="element-130" data-mp="mspppta55185" />
      <motion.div style={{
      y,
      opacity
    }} className="relative z-10 flex flex-col pt-24 will-change-transform md:pt-28" data-id="element-131" data-mp="mtrawqkvf43l">
        <div className="flex flex-1 flex-col px-page" data-id="element-132" data-mp="yto9e2nu4fxd">
          {/* Top: studios / logo — from tablet up; on phones the address moves below the marquee */}
          <div className="hidden items-start justify-between gap-8 md:flex" data-id="element-133" data-mp="13rzfq3ex88au">
            <div data-id="element-134" data-mp="18kskuseaxing">
              <StudioAddress data-id="element-135" data-mp="18lbxpup3hgum" />
            </div>

            <a href="#top" aria-label="Back to top" data-pixel-avoid className="hidden shrink-0 text-white md:block" data-id="element-136" data-mp="1cm47qq5sjpry">
              <BrandLogo className="h-12 w-12" data-id="element-137" data-mp="1hjnr24hcisqr" />
            </a>
          </div>

          {/* Tagline + socials */}
          <div className="mt-auto flex flex-col gap-6 md:flex-row md:items-end md:justify-between md:gap-8 md:pt-44" data-id="element-138" data-mp="1mkbi2q5kr9kz">
            <div data-pixel-avoid data-id="element-139" data-mp="1rgsh4febb8cu">
              <p className="max-w-[640px] font-serif text-[36px] font-normal leading-[1.08] tracking-[-0.01em] md:text-[48px]" data-id="element-140" data-mp="3oboltbvt3bfg">
                {footerInfo.tagline}
              </p>
              <Link to="/contact" className="group mt-6 inline-flex items-center gap-3 md:mt-8 whitespace-nowrap rounded-control border border-white/80 px-6 py-3 text-[14px] uppercase tracking-[0.04em] text-white transition-colors duration-150 hover:border-white hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-141" data-mp="2af3sdp3431j3">
                Contact us
                <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-142" data-mp="2pceb9ji339ou" />
              </Link>
            </div>
            <ul data-pixel-avoid className="flex gap-5" data-id="element-143" data-mp="2fdoszpm9zn82">
              {footerInfo.socials.map(s => <li key={s.label} data-id="element-144" data-mp="18zpfwakicvtr">
                  <a href={s.href} className="text-[15px] transition-opacity duration-150 hover:opacity-70" data-id="element-145" data-mp="13z1jf9difms5">
                    {s.label}
                  </a>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Marquee */}
        <div data-pixel-avoid className="mt-14 overflow-hidden md:mt-20" aria-hidden="true" data-id="element-146" data-mp="2wnr1gqsogn40">
          <motion.div className="flex w-max whitespace-nowrap" animate={reduce ? undefined : {
          x: ['0%', '-50%']
        }} transition={{
          duration: 28,
          ease: 'linear',
          repeat: Infinity
        }} data-id="element-147" data-mp="2iwmbkgf4396v">
            {[0, 1].map(copy => <div key={copy} className="flex items-center" data-id="element-148" data-mp="3fllls0z9al1p">
                {[0, 1].map(i => <span key={i} className="flex items-center text-[15vw] font-normal leading-[0.9] tracking-[-0.03em] text-white md:text-[176px]" data-id="element-149" data-mp="31mc2gsfr7ati">
                    {footerInfo.marquee}
                    <span className="mx-[0.35em] inline-block h-[0.72em] w-[0.72em] bg-white" data-id="element-150" data-mp="1q8gpnqcbne5g" />
                  </span>)}
              </div>)}
          </motion.div>
        </div>

        {/* Phones: address sits under the marquee */}
        <div className="px-page pt-8 md:hidden" data-id="element-151" data-mp="1iltf2s6w1caj">
          <StudioAddress data-id="element-152" data-mp="1wmnmp8m43985" />
        </div>

        <div className="mt-8 border-t border-white/15" data-id="element-153" data-mp="1wlkw71spb8sr" />

        {/* Bottom bar */}
        <div data-pixel-avoid className="flex flex-col gap-2 px-page pb-6 pt-5 text-[12px] md:pb-8 md:pt-8 md:flex-row md:text-[13px] md:items-center md:justify-between md:gap-4 md:py-8" data-id="element-154" data-mp="1rn16cqazlcv4">
          <a href="#top" className="group inline-flex items-center gap-2 whitespace-nowrap transition-opacity duration-150 hover:opacity-70" data-id="element-155" data-mp="zwmybapnmurd">
            <ArrowUpIcon className="h-3.5 w-3.5 transition-transform duration-150 group-hover:-translate-y-0.5" aria-hidden="true" data-id="element-156" data-mp="1dpmihq0dtd59" />
            Back to top
          </a>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-white/75" data-id="element-157" data-mp="g8e12m997yl0">
            <span data-id="element-158" data-mp="g6jllngr4yob">© KAYS VENTURES FZCO 2018–2026 · laebel.co · laebel.io</span>
            {footerInfo.legal.map(l => <Link key={l.label} to={l.href} className="transition-colors duration-150 hover:text-white" data-id="element-159" data-mp="2l96vu6z1r6uq">
                {l.label}
              </Link>)}
          </div>
        </div>
      </motion.div>
    </footer>;
}